/* ================================================================
   Laser Cartridge Exchange PDP — client behaviours

   - Tabs (specifications/manuals) with WAI-ARIA tabset wiring
   - Gallery thumbnails
   - ATC gating on 3 required line item properties (qty hardcoded to 1)
   - Custom flyout-cart-aware Add to Cart (intercepts BEFORE ajaxify-cart.liquid)

   FLYOUT CART INTEGRATION (per `project_theme_conventions.md`):
     The theme uses a custom flyout cart, NOT Dawn's <cart-drawer>.
     Theme-global `snippets/ajaxify-cart.liquid` intercepts every product form
     submit with a capture-phase listener registered in DOMContentLoaded — but
     it then does sessionStorage + reload, which is brittle on this PDP.

     We register OUR submit listener at document level, capture phase, at
     script-load time (before DOMContentLoaded). That guarantees we run before
     ajaxify-cart's listener; stopImmediatePropagation prevents a second handler.

     On success we trigger the flyout by clicking an existing `.flyout-trigger`
     element (the theme's wiring opens the flyout on that click). Falls back to
     sessionStorage + reload if no trigger is in the DOM.
   ================================================================ */
(function () {
  'use strict';

  /* ---------- Custom flyout-aware Add to Cart ---------- */

  // Registered at script-load time (NOT inside DOMContentLoaded) so this
  // attaches before ajaxify-cart.liquid's listener.
  document.addEventListener('submit', handleExchangeSubmit, true);

  function handleExchangeSubmit(e) {
    var form = e.target;
    if (!form || !form.classList || !form.classList.contains('exchange-pdp__form')) return;

    // Own the cart-add flow — block ajaxify-cart.liquid and the default form post.
    e.preventDefault();
    e.stopImmediatePropagation();

    // Ignore rapid repeat submits while either cart request is in flight.
    if (form.dataset.exchangeSubmitting === '1') return;

    var submit = form.querySelector('[data-exchange-submit]');
    if (!submit) return;
    if (submit.dataset.soldOut === 'true') return;

    var error = form.querySelector('[data-exchange-error]');
    var required = form.querySelectorAll('[data-exchange-required]');

    // Trim-check required fields. HTML5 `required` lets " " through; we don't want that.
    for (var i = 0; i < required.length; i++) {
      if (!required[i].value.trim()) {
        if (error) error.setAttribute('aria-hidden', 'false');
        required[i].focus();
        return;
      }
    }
    if (error) error.setAttribute('aria-hidden', 'true');

    var originalLabel = submit.textContent;
    var formData = new FormData(form);
    formData.set('quantity', '1');
    required.forEach(function (field) {
      formData.set(field.name, field.value.trim());
    });
    form.dataset.exchangeSubmitting = '1';
    submit.disabled = true;
    submit.textContent = 'Checking cart…';

    // A second add with the same variant and registration identifiers would
    // merge into quantity 2. Keep each registered exchange at quantity 1 while
    // allowing the same variant with a different identifier set as a new line.
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart-check failed (' + response.status + ')');
        return response.json();
      })
      .then(function (cart) {
        var variantId = String(formData.get('id'));
        var model = String(formData.get('properties[_ULS Model]') || '').trim();
        var machineSerial = String(formData.get('properties[_ULS Serial Number]') || '').trim();
        var cartridgeSerial = String(formData.get('properties[_Cartridge Serial Number]') || '').trim();
        var duplicate = cart.items.some(function (item) {
          var props = item.properties || {};
          return String(item.variant_id) === variantId &&
            String(props['_ULS Model'] || '').trim() === model &&
            String(props['_ULS Serial Number'] || '').trim() === machineSerial &&
            String(props['_Cartridge Serial Number'] || '').trim() === cartridgeSerial;
        });
        if (duplicate) {
          var duplicateError = new Error('duplicate exchange registration');
          duplicateError.code = 'DUPLICATE_EXCHANGE';
          throw duplicateError;
        }

        submit.textContent = 'Adding…';
        return fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' }
        });
      })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (body) {
            throw new Error(body && body.description ? body.description : 'cart-add failed (' + response.status + ')');
          });
        }
        return response.json();
      })
      .then(function () {
        submit.textContent = 'Added';

        // No-reload flyout open. Progressive fallback chain:
        //   1. window.openFlyoutCart()    — formal convention (flyout-cart-js.liquid exposes it)
        //   2. .flyout-trigger click      — works even on themes without the global exposure
        //   3. sessionStorage + reload    — last resort if no flyout markup is on the page
        var trigger;
        if (typeof window.openFlyoutCart === 'function') {
          window.openFlyoutCart();
        } else if ((trigger = document.querySelector('.flyout-trigger'))) {
          trigger.click();
        } else {
          sessionStorage.setItem('openFlyout', 'true');
          window.location.reload();
          return;
        }

        // Restore button after a brief beat
        setTimeout(function () {
          delete form.dataset.exchangeSubmitting;
          submit.disabled = false;
          submit.textContent = originalLabel;
          if (required.length) required[0].dispatchEvent(new Event('input', { bubbles: true }));
        }, 1200);
      })
      .catch(function (err) {
        console.error('[exchange-pdp] ATC error:', err);
        delete form.dataset.exchangeSubmitting;
        submit.disabled = false;
        submit.textContent = originalLabel;
        if (error) {
          if (err && err.code === 'DUPLICATE_EXCHANGE') {
            error.innerHTML = 'This exact exchange registration is already in your cart. <a href="/cart">View cart</a> or enter a different set of identifiers.';
          } else {
            error.textContent = err && err.message
              ? 'Could not add to cart: ' + err.message
              : 'Could not add to cart. Please try again or contact hello@abernethybeck.com.';
          }
          error.setAttribute('aria-hidden', 'false');
        }
      });
  }

  /* ---------- DOM-ready init for tabs / gallery / button-state UX ---------- */

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function initOne(root) {
    if (!root || root.dataset.exchangePdpInit === '1') return;
    root.dataset.exchangePdpInit = '1';

    initTabs(root);
    initGallery(root);
    initFieldUx(root);
    initHelpPanels(root);
    initQuoteLink(root);
  }

  /* ---------- Tabs (WAI-ARIA tabset pattern) ----------
     - Click activates a tab + shows its panel
     - Roving tabindex: only the active tab is in the tab order (tabindex=0); others -1
     - Keyboard nav: Left/Right cycles, Home/End jumps to first/last
     - aria-selected, aria-controls, and aria-labelledby wiring lives in the Liquid */
  function initTabs(root) {
    var tabBtns = Array.prototype.slice.call(root.querySelectorAll('[data-exchange-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-exchange-panel]'));
    if (!tabBtns.length) return;

    function activate(idx, focusTab) {
      var target = tabBtns[idx].getAttribute('data-exchange-tab');
      tabBtns.forEach(function (b, i) {
        var active = i === idx;
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach(function (p) {
        var match = p.getAttribute('data-exchange-panel') === target;
        if (match) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });
      if (focusTab) tabBtns[idx].focus();
    }

    tabBtns.forEach(function (btn, idx) {
      btn.addEventListener('click', function () {
        activate(idx, false);
      });
      btn.addEventListener('keydown', function (e) {
        var newIdx = -1;
        switch (e.key) {
          case 'ArrowRight':
            newIdx = (idx + 1) % tabBtns.length;
            break;
          case 'ArrowLeft':
            newIdx = (idx - 1 + tabBtns.length) % tabBtns.length;
            break;
          case 'Home':
            newIdx = 0;
            break;
          case 'End':
            newIdx = tabBtns.length - 1;
            break;
          default:
            return;
        }
        e.preventDefault();
        activate(newIdx, true);
      });
    });
  }

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    var thumbs = root.querySelectorAll('[data-exchange-thumb]');
    var slides = root.querySelectorAll('[data-exchange-slide]');
    if (!thumbs.length || !slides.length) return;

    function activate(idx) {
      thumbs.forEach(function (t, i) {
        t.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
      slides.forEach(function (s, i) {
        s.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
      });
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(i); });
    });
  }

  /* ---------- Field UX (button disabled-until-filled, helper toggle) ----------
     This handles the *visual* state of the ATC button and helper line.
     The actual submit interception happens in handleExchangeSubmit() above,
     which runs at script-load time. */
  function initFieldUx(root) {
    var form = root.querySelector('.exchange-pdp__form');
    if (!form) return;

    var submit = form.querySelector('[data-exchange-submit]');
    var helper = form.querySelector('[data-exchange-atc-helper]');
    var error = form.querySelector('[data-exchange-error]');
    var required = form.querySelectorAll('[data-exchange-required]');
    if (!submit || !required.length) return;

    var soldOut = submit.dataset.soldOut === 'true';

    function allFilled() {
      for (var i = 0; i < required.length; i++) {
        if (!required[i].value.trim()) return false;
      }
      return true;
    }

    function updateState() {
      if (soldOut) {
        if (helper) helper.classList.add('is-hidden');
        return;
      }
      var ok = allFilled() && form.dataset.exchangeSubmitting !== '1';
      submit.disabled = !ok;
      submit.setAttribute('aria-disabled', ok ? 'false' : 'true');
      if (helper) {
        helper.classList.toggle('is-hidden', ok);
        if (!ok) {
          var missing = [];
          for (var i = 0; i < required.length; i++) {
            if (!required[i].value.trim()) missing.push(required[i].dataset.exchangeLabel);
          }
          helper.textContent = 'Still needed: ' + missing.join(', ') + '.';
        }
      }
    }

    required.forEach(function (f) {
      f.addEventListener('input', function () {
        if (error) error.setAttribute('aria-hidden', 'true');
        updateState();
      });
      f.addEventListener('blur', updateState);
    });

    updateState();
  }

  /* ---------- Inline identification help ---------- */
  function initHelpPanels(root) {
    var toggles = root.querySelectorAll('[data-exchange-help-toggle]');
    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var id = toggle.getAttribute('aria-controls');
        var panel = id ? document.getElementById(id) : null;
        if (!panel) return;
        var willOpen = panel.hasAttribute('hidden');
        if (willOpen) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
        root.querySelectorAll('[data-exchange-help-toggle][aria-controls="' + id + '"]').forEach(function (peer) {
          peer.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        });
      });
    });
  }

  /* ---------- Quote quantity/prefill URL ---------- */
  function initQuoteLink(root) {
    var qty = root.querySelector('[data-exchange-quote-qty]');
    var link = root.querySelector('[data-exchange-quote]');
    if (!qty || !link) return;

    function update() {
      var value = qty.value === '5+' ? '5' : qty.value;
      var reason = value === '1' ? 'po' : 'multi';
      link.href = link.dataset.baseUrl + '?type=exchange&product=' + link.dataset.productHandle + '&qty=' + encodeURIComponent(value) + '&reason=' + reason;
    }

    qty.addEventListener('change', update);
    update();
  }

  ready(function () {
    document.querySelectorAll('.exchange-pdp').forEach(initOne);
  });

  // Re-init on Shopify section reload (theme editor)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target.querySelector('.exchange-pdp') || e.target.closest('.exchange-pdp');
      if (root) initOne(root);
    });
  }
})();
